import { useState, useMemo } from 'react'
import {
  Plus,
  Pencil,
  Trash2,
  FolderOpen,
  ChevronRight,
  ChevronDown,
  Search,
  Loader2,
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useProjects, useDeleteProject } from '../api/useProjects'
import { ProjectDialog } from '../components/ProjectDialog'
import ConfirmDialog from '@/components/shared/ConfirmDialog'
import { logger } from '@/lib/utils/logger'
import type { Project } from '@/types/project.types'

interface ProjectTreeNode extends Project {
  children: ProjectTreeNode[]
  level: number
}

function buildTree(projects: Project[]): ProjectTreeNode[] {
  const map = new Map<number, ProjectTreeNode>()
  const roots: ProjectTreeNode[] = []

  for (const p of projects) {
    map.set(p.id, { ...p, children: [], level: 0 })
  }

  for (const node of map.values()) {
    if (node.parent_id && map.has(node.parent_id)) {
      const parent = map.get(node.parent_id)!
      node.level = parent.level + 1
      parent.children.push(node)
    } else {
      roots.push(node)
    }
  }

  const sortNodes = (nodes: ProjectTreeNode[]): ProjectTreeNode[] => {
    nodes.sort((a, b) => a.name.localeCompare(b.name))
    for (const n of nodes) sortNodes(n.children)
    return nodes
  }

  return sortNodes(roots)
}

function flattenVisible(
  nodes: ProjectTreeNode[],
  expanded: Set<number>
): ProjectTreeNode[] {
  const result: ProjectTreeNode[] = []
  for (const node of nodes) {
    result.push(node)
    if (node.children.length > 0 && expanded.has(node.id)) {
      result.push(...flattenVisible(node.children, expanded))
    }
  }
  return result
}

const TYPE_COLORS: Record<string, string> = {
  platform: 'bg-purple-100 text-purple-800',
  product: 'bg-blue-100 text-blue-800',
  service: 'bg-green-100 text-green-800',
  module: 'bg-orange-100 text-orange-800',
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export default function ProjectsPage() {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create')
  const [selectedProject, setSelectedProject] = useState<Project | undefined>()
  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  const [searchQuery, setSearchQuery] = useState('')

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState<Project | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const { data: projects, isLoading, error } = useProjects()
  const deleteMutation = useDeleteProject()

  const tree = useMemo(() => {
    if (!projects) return []
    return buildTree(projects)
  }, [projects])

  const visibleRows = useMemo(() => {
    const rows = flattenVisible(tree, expanded)
    if (!searchQuery.trim()) return rows
    const q = searchQuery.toLowerCase()
    return rows.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.prefix && p.prefix.toLowerCase().includes(q)) ||
        (p.description && p.description.toLowerCase().includes(q))
    )
  }, [tree, expanded, searchQuery])

  const projectMap = useMemo(() => {
    if (!projects) return new Map<number, Project>()
    return new Map(projects.map((p) => [p.id, p]))
  }, [projects])

  const toggleExpand = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleCreate = () => {
    setSelectedProject(undefined)
    setDialogMode('create')
    setDialogOpen(true)
  }

  const handleEdit = (project: Project) => {
    setSelectedProject(project)
    setDialogMode('edit')
    setDialogOpen(true)
  }

  const handleDeleteClick = (project: Project) => {
    setDeleteTarget(project)
    setDeleteDialogOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return
    try {
      await deleteMutation.mutateAsync(deleteTarget.id)
      toast.success(`Project "${deleteTarget.name}" deleted`)
      setDeleteDialogOpen(false)
      setDeleteTarget(null)
    } catch (err) {
      logger.error('Failed to delete project', err)
      toast.error('Failed to delete project')
    }
  }

  const getParentName = (parentId: number | null | undefined): string => {
    if (!parentId) return '—'
    const parent = projectMap.get(parentId)
    return parent ? `${parent.name} (${parent.prefix})` : '—'
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Projects</h2>
          <p className="text-muted-foreground">
            Manage test projects and organize test cases
          </p>
        </div>
        <Button onClick={handleCreate} className="bg-[#1976D2] hover:bg-[#1565C0]">
          <Plus className="mr-2 h-4 w-4" />
          New Project
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search projects..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Error */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>
            {error instanceof Error ? error.message : 'Failed to load projects'}
          </AlertDescription>
        </Alert>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Loading projects...</span>
        </div>
      )}

      {/* Empty */}
      {!isLoading && !error && projects && projects.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-12">
          <FolderOpen className="h-12 w-12 text-muted-foreground" />
          <h3 className="mt-4 text-lg font-semibold">No projects yet</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Get started by creating your first project
          </p>
          <Button
            onClick={handleCreate}
            className="mt-4 bg-[#1976D2] hover:bg-[#1565C0]"
          >
            <Plus className="mr-2 h-4 w-4" />
            Create Project
          </Button>
        </div>
      )}

      {/* Table */}
      {!isLoading && !error && visibleRows.length > 0 && (
        <div className="rounded-lg border bg-white">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Project Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Prefix</TableHead>
                <TableHead>Parent</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleRows.map((node) => {
                const hasChildren = node.children.length > 0
                const isExpanded = expanded.has(node.id)
                const indent = node.level * 24

                return (
                  <TableRow key={node.id}>
                    <TableCell>
                      <div
                        className="flex items-center gap-2"
                        style={{ paddingLeft: `${indent}px` }}
                      >
                        {hasChildren ? (
                          <button
                            onClick={() => toggleExpand(node.id)}
                            className="p-0.5 hover:bg-gray-100 rounded"
                          >
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            )}
                          </button>
                        ) : (
                          <div className="w-5" />
                        )}
                        <span className="font-medium">{node.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {node.project_type ? (
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            TYPE_COLORS[node.project_type] || 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {node.project_type.charAt(0).toUpperCase() +
                            node.project_type.slice(1)}
                        </span>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-sm text-muted-foreground">
                      {node.prefix || '—'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {getParentName(node.parent_id)}
                    </TableCell>
                    <TableCell className="text-muted-foreground max-w-xs truncate">
                      {node.description || '—'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(node.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(node)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteClick(node)}
                          disabled={deleteMutation.isPending}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* No search results */}
      {!isLoading &&
        !error &&
        projects &&
        projects.length > 0 &&
        visibleRows.length === 0 &&
        searchQuery.trim() && (
          <div className="text-center py-8 text-muted-foreground">
            No projects match "{searchQuery}"
          </div>
        )}

      {/* Dialogs */}
      <ProjectDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        project={selectedProject}
        mode={dialogMode}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Project"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? This will remove the project and all associated data. This action cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={handleConfirmDelete}
      />
    </div>
  )
}
